import React, { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import api from "../../api/axios";
import Swal from "sweetalert2";

const LiveLogs = () => {
  const [employees, setEmployees] = useState([]);
  const [logs, setLogs] = useState([]);
  const [mergedLogs, setMergedLogs] = useState([]);
  const [rosters, setRosters] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // ✅ Fetch Employees
  const fetchEmployees = async () => {
    try {
      const res = await api.get("/hrms/employees");
      setEmployees(res.data.data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      Swal.fire("Error", "Failed to fetch employees", "error");
    }
  };

  // ✅ Fetch Logs
  const fetchLogs = async () => {
    try {
      const res = await api.get("/hrms/devices/alllogs");
      setLogs(res.data.data || []);
    } catch (error) {
      console.error("Error fetching logs:", error);
      Swal.fire("Error", "Failed to fetch logs", "error");
    }
  };

  const fetchRosters = async () => {
    try {
      const res = await api.get("/hrms/rosters");
      setRosters(res.data.data || {});
    } catch (error) {
      Swal.fire("Error", "Failed to fetch duty roster", "error");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEmployees(), fetchLogs(), fetchRosters()]);
      setLoading(false);
    };

    loadData();
  }, []);

  useEffect(() => {
    if (employees.length === 0 || logs.length === 0) return;

    const combined = logs.map((log) => {
      const emp = employees.find((e) => e.employee_code === log.employee_code);

      return {
        id: log.id,
        device_id: log.device_id,
        employeeName: emp
          ? emp.first_name + " " + (emp.last_name || "")
          : "Unknown",
        department: emp ? emp.department || "N/A" : "N/A",
        punchTime: formatTime(log.punch_at),
        date: formatDate(log.punch_at),
      };
    });

    setMergedLogs(combined);
  }, [employees, logs]);

  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    const timePart = timestamp.slice(11, 16);
    const [hourStr, minute] = timePart.split(":");
    const hour = parseInt(hourStr);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minute} ${ampm}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const datePart = timestamp.split("T")[0];
    const [year, month, day] = datePart.split("-");

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return `${day} ${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const combinedLogs = logs.map((log) => {
    const emp = employees.find((e) => e.employee_code === log.employee_code);
    const dateKey = log.punch_at.split("T")[0];
    let dutyStart = null;

    if (rosters[dateKey]) {
      Object.values(rosters[dateKey]).forEach((shiftObj) => {
        const match = shiftObj.employees.find(
          (e) => e.employee_code === log.employee_code
        );
        if (match) dutyStart = match.start_time;
      });
    }
    return {
      id: log.id,
      employeeName: emp
        ? `${emp.first_name} ${emp.last_name || ""}`
        : "Unknown",
      employeeCode: emp ? emp.employee_code || "N/A" : "N/A",
      department: emp ? emp.department || "N/A" : "N/A",
      outlet: emp ? emp.outlet || "N/A" : "N/A",
      propertyCode: emp ? emp.property_code || "N/A" : "N/A",
      punchTime: formatTime(log.punch_at),
      date: formatDate(log.punch_at),
    };
  });

  const filteredLogs = combinedLogs.filter((log) => {
    const nameMatch = log.employeeName
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const codeMatch = log.employeeCode
      ?.toLowerCase()
      .includes(search.toLowerCase());

    return nameMatch || codeMatch;
  });

  // if (loading) {
  //   return (
  //     <div className="row">
  //       <div className="col-12">
  //         <div className="live-logs-card">
  //           <div className="d-flex align-items-center gap-2">
  //             <Activity size={30} stroke="#302867" />
  //             <div className="d-flex flex-column">
  //               <h5 className="fs-4 fw-semibold m-0">Live Attendance Logs</h5>
  //               <span className="text-muted fs-6">
  //                 Real-time feed of attendance punches for your property.
  //               </span>
  //             </div>
  //           </div>
  //           <div
  //             className="d-flex justify-content-center align-items-center"
  //             style={{ height: "300px" }}
  //           >
  //             <div className="spinner-border text-primary" role="status">
  //               <span className="visually-hidden">Loading...</span>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row">
        <div className="col-12">
          <div className="live-logs-card">
            <div className="d-flex align-items-center gap-2">
              <Activity size={30} stroke="#302867" />
              <div className="d-flex flex-column">
                <h5 className="fs-4 fw-semibold m-0">Live Attendance Logs</h5>
                <span className="text-muted fs-6">
                  Real-time feed of attendance punches for your property.
                </span>
              </div>
            </div>
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "300px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive mt-4">
                <div className="d-flex mb-3">
                  <input
                    type="text"
                    className="form-control w-25"
                    placeholder="Search by name or code..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <table className="table align-middle mb-0 table-hover">
                  <thead>
                    <tr className="text-muted small border-top">
                      <th>Employee Code</th>
                      <th>Employee Name</th>
                      <th>Department</th>
                      <th>Outlet</th>
                      <th>Punch Time</th>
                      <th>Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredLogs.length > 0 ? (
                      filteredLogs.map((log) => (
                        // {combinedLogs.length > 0 ? (
                        //   combinedLogs.map((log) => (
                        <tr key={log.id}>
                          <td>{log.employeeCode}</td>
                          <td>{log.employeeName}</td>
                          <td>{log.department}</td>
                          <td>{log.outlet}</td>
                          <td>{log.punchTime}</td>
                          <td>{log.date}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-3">
                          No Matching Records
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveLogs;
